import { type ReactNode } from 'react';
import { cn } from '@reflex/ui/lib/utils';

export interface DataTableColumn<T> {
  header: string;
  /**
   * Screen-reader-only header for columns whose meaning is purely visual
   * (trailing action buttons, status badges). An empty `header` with no
   * `srHeader` fails the storybook axe gate (`empty-table-header`).
   */
  srHeader?: string;
  cell: (item: T) => ReactNode;
  headerClassName?: string;
  cellClassName?: string;
}

interface DataTableProps<T> {
  data: T[];
  columns: DataTableColumn<T>[];
  rowKey: (item: T) => string;
  onRowClick?: (item: T) => void;
  className?: string;
}

export function DataTable<T>({ data, columns, rowKey, onRowClick, className }: DataTableProps<T>) {
  return (
    <div className={cn('overflow-x-auto rounded-xl border border-border', className)}>
      <table className="w-full text-sm">
        <thead>
          <tr className="bg-muted/50 text-left text-xs text-muted-foreground">
            {columns.map((col) => (
              <th
                key={col.header || col.srHeader}
                className={cn('px-4 py-2.5 font-medium', col.headerClassName)}
              >
                {col.header ||
                  (col.srHeader ? <span className="sr-only">{col.srHeader}</span> : null)}
              </th>
            ))}
          </tr>
        </thead>
        <tbody className="divide-y divide-border">
          {data.map((item) => (
            <tr
              key={rowKey(item)}
              className={cn('transition-colors hover:bg-muted/30', onRowClick && 'cursor-pointer')}
              onClick={onRowClick ? () => onRowClick(item) : undefined}
            >
              {columns.map((col) => (
                <td key={col.header} className={cn('px-4 py-3', col.cellClassName)}>
                  {col.cell(item)}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
